import dayjs, { Dayjs } from 'dayjs'
import React from 'react'
import { Controller, Path, PathValue, UseFormReturn } from 'react-hook-form'
import DateInput from './DateInput'

interface DateStepInputProps<T extends Record<string, any>> {
	form: UseFormReturn<T>
	fieldName: Path<T>
	minDate: Dayjs
	maxDate: Dayjs
	label: string
	description?: string
}

const DateStepInput = <T extends Record<string, any>>({
	form,
	fieldName,
	minDate,
	maxDate,
	label,
	description,
}: DateStepInputProps<T>) => {
	const onChange = (value: string) => {
		const date = dayjs(value)

		if (date.isAfter(maxDate)) {
			return form.setValue(
				fieldName as Path<T>,
				maxDate.format('YYYY-MM-DD') as PathValue<T, Path<T>>,
				{
					shouldDirty: true,
				}
			)
		}

		if (date.isBefore(minDate)) {
			return form.setValue(
				fieldName as Path<T>,
				minDate.format('YYYY-MM-DD') as PathValue<T, Path<T>>,
				{
					shouldDirty: true,
				}
			)
		}

		form.setValue(fieldName as Path<T>, value as PathValue<T, Path<T>>, { shouldDirty: true })
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{description && <div className='text-xs text-text-dark dark:text-white'>{description}</div>}

			<Controller
				control={form.control}
				name={fieldName}
				defaultValue={minDate.format('YYYY-MM-DD') as PathValue<T, Path<T>>}
				render={({ field }) => (
					<DateInput
						{...field}
						minDate={minDate.format('YYYY-MM-DD')}
						maxDate={maxDate.format('YYYY-MM-DD')}
						label={label}
						value={field.value || minDate.format('YYYY-MM-DD')}
						onChange={onChange}
						onBlur={onChange}
					/>
				)}
			/>
		</div>
	)
}

export default DateStepInput
